import React, { useState } from 'react'
import { Eye, TrendingUp, Clock, Check } from 'lucide-react'
import { api } from '../lib/api'
import { fmtPrice, fmtSignalType, timeAgo } from '../lib/utils'

/**
 * Monitoring alerts — recent bullish signals on trade_enabled=false symbols.
 *
 * These 102 symbols are collected and get signals generated exactly like the
 * 265 trade-enabled ones, but the scanner never places an order for them. When
 * one throws a strong BUY signal (confidence >= 0.55, same bar the backend
 * filters at), it's worth a human glance: is this a name that should be
 * trading? The "Enable Trading" button calls the same endpoint the Watchlist
 * page's manual toggle uses -- this panel is a shortcut to that decision, not
 * a separate mechanism.
 *
 * Backend endpoint (/api/watchlist/monitoring-alerts) shipped 2026-03-10 with
 * no UI ever wired to it -- found orphaned during the 2026-08-27/28 redundancy
 * audit and built out here rather than deleted, since it's a real, working
 * feature that just never got a surface.
 */
export default function MonitoringAlerts({ alerts = [], loading, onEnabled }) {
  const [acting, setActing] = useState(null)   // symbol currently being enabled
  const [justEnabled, setJustEnabled] = useState(new Set())

  const handleEnable = async (symbol) => {
    setActing(symbol)
    try {
      const res = await api.setTradeEnabled(symbol, true)
      if (res.success) {
        setJustEnabled(prev => new Set(prev).add(symbol))
        onEnabled?.(symbol)
      }
    } catch { /* leave the row as-is, operator can retry */ }
    finally {
      setActing(null)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <h3 className="section-header">Monitoring Alerts</h3>
        <div className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="h-11 rounded-lg animate-pulse" style={{ background: '#1a2235' }} />
          ))}
        </div>
      </div>
    )
  }

  if (!alerts.length) {
    return (
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <h3 className="section-header">Monitoring Alerts</h3>
        <div className="flex flex-col items-center justify-center py-10 text-slate-700">
          <Eye className="h-9 w-9 mb-2 opacity-20" />
          <p className="text-sm text-slate-600">No strong signals on monitoring-only symbols</p>
          <p className="text-xs text-slate-800 mt-1">(102 symbols tracked, not traded — last 24h)</p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-header" style={{ marginBottom: 0 }}>
          Monitoring Alerts
        </h3>
        <span
          className="text-slate-700 tabular-nums"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}
        >
          {alerts.length} alert{alerts.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-slate-700 mb-3" style={{ fontSize: '0.68rem' }}>
        Bullish signals on symbols tracked but not trading — review and enable if warranted.
      </p>

      <div className="space-y-1 max-h-80 overflow-y-auto pr-0.5">
        {alerts.map((a, i) => {
          const enabled = justEnabled.has(a.symbol)
          const confidencePct = Math.round(Number(a.confidence) * 100)

          return (
            <div
              key={a.id ?? i}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
              style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}
            >
              <div
                className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded"
                style={{
                  background: 'rgba(16,185,129,0.12)',
                  border: '1px solid rgba(16,185,129,0.2)',
                  boxShadow: '0 0 6px rgba(16,185,129,0.2)',
                }}
              >
                <TrendingUp className="h-3 w-3 text-emerald-400" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-200 text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {a.symbol}
                  </span>
                  <span className="text-slate-700" style={{ fontSize: '0.65rem' }}>
                    {a.sector || a.sector_etf || ''}
                  </span>
                </div>
                <p className="text-slate-700 truncate mt-0.5" style={{ fontSize: '0.68rem' }}>
                  {fmtSignalType(a.signal_type)} · confidence {confidencePct}%
                </p>
              </div>

              <div className="flex-shrink-0 text-right hidden sm:block">
                <p className="text-slate-300 text-sm tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {fmtPrice(a.price)}
                </p>
                <p className="text-slate-700 flex items-center justify-end gap-0.5 mt-0.5" style={{ fontSize: '0.65rem' }}>
                  <Clock className="h-2.5 w-2.5" />
                  {timeAgo(a.created_at)}
                </p>
              </div>

              <button
                onClick={() => handleEnable(a.symbol)}
                disabled={enabled || acting === a.symbol}
                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-md font-semibold transition-colors"
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.62rem',
                  letterSpacing: '0.03em',
                  background: enabled ? 'rgba(16,185,129,0.12)' : 'rgba(34,211,238,0.08)',
                  border: `1px solid ${enabled ? 'rgba(16,185,129,0.3)' : 'rgba(34,211,238,0.2)'}`,
                  color: enabled ? '#10b981' : '#22d3ee',
                  cursor: enabled || acting === a.symbol ? 'default' : 'pointer',
                  opacity: acting === a.symbol ? 0.6 : 1,
                }}
              >
                {enabled
                  ? <><Check className="h-3 w-3" /> ENABLED</>
                  : acting === a.symbol ? '...' : 'ENABLE'}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
